import { defineComponent as _defineComponent } from 'vue'
import AnimalRxWidget from './animalrx-widget/AnimalRxWidget.vue';
import BannerWidget from './banner-widget/BannerWidget.vue';
import BannerX from 'JS/sponsored/components/BannerX.vue';
import BlogCarouselWidget from './blog-carousel-widget/BlogCarouselWidget.vue';
import BrandBannerWidget from './brand-banner-widget/BrandBannerWidget.vue';
import BrandLogosWidget from './brand-logos-widget/BrandLogosWidget.vue';
import BuyItAgainCarousel from 'JS/components/carousels/BuyItAgainCarousel.vue';
import CardWidget from './card-widget/CardWidget.vue';
import DxpContainer from './DxpContainer.vue';
import FeaturedCategoriesWidget from './featured-categories-widget/FeaturedCategoriesWidget.vue';
import ItemCarouselWidget from './item-carousel-widget/ItemCarouselWidget.vue';
import PageSeoTextWidget from './shared/PageSeoTextWidget.vue';
import PrebuiltHeaderWidget from './prebuilt-header-widget/PrebuiltHeaderWidget.vue';
import SeoTextWidget from './toyland/SEOTextWidget.vue';
import SponsoredCarousel from 'JS/sponsored/components/SponsoredCarousel.vue';
import SsrRoot from 'JS/ssr/SsrRoot.vue';
import StoryCarouselWidget from './story-carousel-widget/StoryCarouselWidget.vue';
import TileGridWidget from './tile-grid-widget/TileGridWidget.vue';
import ToylandBanner from './toyland/ToylandBanner.vue';
import ToylandInSeasonBanner from './toyland/ToylandInSeasonBanner.vue';
import ToylandSignup from './toyland/ToylandSignup.vue';
import TrendingCarouselWidget from './trending-carousel-widget/TrendingCarouselWidget.vue';
import WinYourWishlistWidget from './win-your-wishlist-widget/WinYourWishlistWidget.vue';
import { computed, defineComponent, ExtractPropTypes, h, onMounted } from 'vue';
import { DxpInsertedContent, DxpInsertedContentType } from '../types/insertions';
import { DxpPageSsrRequest } from '../types/page';
import { PrebuiltHeaderVariant } from 'JS/dxp/types/widgets/prebuilt-header-widget';
import { provideDxpAccentColor } from '../composables/colors';
import { useDxpPageTitle } from '../composables/title';
import { WidgetResource, WidgetType } from '../types/widgets';


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  props: {
    requestData: null
  },
  setup(__props: any) {

const props = __props as {
	requestData: DxpPageSsrRequest,
}



const page = computed(()=>  props.requestData?.resource);

const accentColor = computed(() => page.value.accentColor);
provideDxpAccentColor(accentColor);

const isToyland = computed(() => {
	return page.value.slug === "toyland";
});

const isWrenwoodPage = computed(() => {
	return page.value.slug === "brands/wrenwood";
});

const isBrandPage = computed(() => {
	return page.value.slug.startsWith("brands/");
});

const suppressPageBodyText = computed(() => {
	return props.requestData?.suppressPageBodyText;
});

const suppressTopPadding = computed(() => {
	const topWidgetType = page.value.widgets[0]?.type;
	return topWidgetType === WidgetType.PrebuiltHeaderWidget;
});

const mixedWidgets = computed(() => {
	const widgetsAndInsertions: DxpInsertedContent[] = props.requestData.resource.widgets.map((widget) => {
		return { type: DxpInsertedContentType.Widget, data: widget, id: `dxp-widget-${widget.widget.id}` };
	});

	if (props.requestData.buyItAgainCarousel?.products?.length > 3) {
		widgetsAndInsertions.splice(1, 0, { type: DxpInsertedContentType.BuyItAgain, data: props.requestData.buyItAgainCarousel, id: `dxp-buy-again` });
	}

	if (props.requestData.sponsoredCarousel?.products?.length > 5) {
		widgetsAndInsertions.splice(
			getPagePlacementIndex(DxpInsertedContentType.SponsoredCarousel, widgetsAndInsertions.length, null),
			0,
			{ type: DxpInsertedContentType.SponsoredCarousel, data: props.requestData.sponsoredCarousel, id: `dxp-sponsored-carousel` }
		);
	}

	props.requestData.sponsoredBanners?.forEach(banner => {
		widgetsAndInsertions.splice(
			getPagePlacementIndex(DxpInsertedContentType.SponsoredBanner, widgetsAndInsertions.length, banner.slot),
			0,
			{ type: DxpInsertedContentType.SponsoredBanner, data: banner, id: `dxp-sponsored-banner-${banner.slot}` }
		);
	});

	return widgetsAndInsertions;
});

onMounted(() => {
	if (window.setupVwoSmartCode) {
		window.setupVwoSmartCode();
	}
});

function getPagePlacementIndex(type: DxpInsertedContentType, currentArrLength: number, slotID: string) {
	if (page.value.slug === "/") {
		if (type === DxpInsertedContentType.SponsoredBanner) {
			if (slotID === "Middle") {
				return 2;
			} else if (slotID === "Bottom") {
				return currentArrLength;
			}
		} else if (type === DxpInsertedContentType.SponsoredCarousel) {
			return currentArrLength;
		}
	} else {
		if (type === DxpInsertedContentType.SponsoredBanner) {
			return 2;
		} else if (type === DxpInsertedContentType.SponsoredCarousel) {
			return 5;
		}
	}

	return currentArrLength;
}

const title = useDxpPageTitle(() => page.value.title);

const shouldUseDefaultH1 = computed(() => !isHeaderWidget(page.value.widgets?.[0]) && page.value.slug !== '/');

function isHeaderWidget(widget: WidgetResource | null | undefined): boolean {
	if (widget == null) {
		return false;
	}

	return (
		(widget.type === WidgetType.PrebuiltHeaderWidget && widget.widget.variant !== PrebuiltHeaderVariant.Wrenwood)
			|| (widget.type === WidgetType.FeaturedCategoriesWidget && widget.data.shouldActAsPageHeader)
	);
}

const VNodes = defineComponent({
	functional: true,
	render: (_h, ctx) => ctx.props.vnodes
});

const widgetTypeToComponent = {
	[WidgetType.BrandLogosWidget]: BrandLogosWidget,
	[WidgetType.FeaturedCategoriesWidget]: FeaturedCategoriesWidget,
	[WidgetType.CardWidget]: CardWidget,
	[WidgetType.BannerWidget]: BannerWidget,
	[WidgetType.AnimalRxBannerWidget]: AnimalRxWidget,
	[WidgetType.PrebuiltHeaderWidget]: PrebuiltHeaderWidget,
	[WidgetType.TrendingCarouselWidget]: TrendingCarouselWidget,
	[WidgetType.BlogCarouselWidget]: BlogCarouselWidget,
	[WidgetType.StoryCarouselWidget]: StoryCarouselWidget,
	[WidgetType.ItemCarouselWidget]: ItemCarouselWidget,
	[WidgetType.TileGridWidget]: TileGridWidget,
	[WidgetType.WinYourWishlistWidget]: WinYourWishlistWidget,
	[WidgetType.BrandBannerWidget]: BrandBannerWidget,
};

/**
 * Simple wrapper to ensure props type matches component
 */
function renderWidgetComponent<T extends (typeof widgetTypeToComponent[keyof typeof widgetTypeToComponent])>(component: T, props: ExtractPropTypes<T['props']>) {
	return h(component, { props }); // Vue 2 (uncertain about 3) doesn't strongly type the `props` option in the `h` fn
}

return { __sfc: true,props, page, accentColor, isToyland, isWrenwoodPage, isBrandPage, suppressPageBodyText, suppressTopPadding, mixedWidgets, getPagePlacementIndex, title, shouldUseDefaultH1, isHeaderWidget, VNodes, widgetTypeToComponent, renderWidgetComponent, BannerX, BuyItAgainCarousel, DxpContainer, PageSeoTextWidget, SeoTextWidget, SponsoredCarousel, SsrRoot, ToylandBanner, ToylandInSeasonBanner, ToylandSignup, DxpInsertedContentType }
}

})